<template>
  <div>
    <div class="d-flex justify-content-between align-items-center flex-wrap mb-2">
      <div>
        <h2 class="mb-0">CRM - Oportunidades</h2>
        <small class="text-muted">Pipeline de vendas e oportunidades</small>
      </div>
      <div class="d-flex align-items-center" style="gap: 0.5rem; flex-wrap: wrap;">
        <b-button
          :variant="viewMode === 'kanban' ? 'primary' : 'outline-secondary'"
          size="sm"
          @click="switchView('kanban')"
        >
          <feather-icon icon="ColumnsIcon" size="14" class="mr-25" /> Kanban
        </b-button>
        <b-button
          :variant="viewMode === 'lista' ? 'primary' : 'outline-secondary'"
          size="sm"
          @click="switchView('lista')"
        >
          <feather-icon icon="ListIcon" size="14" class="mr-25" /> Lista
        </b-button>
        <b-button variant="primary" size="sm" @click="openModal()">
          <feather-icon icon="PlusIcon" size="14" class="mr-25" /> Nova Oportunidade
        </b-button>
      </div>
    </div>

    <!-- KANBAN VIEW -->
    <div v-if="viewMode === 'kanban'">
      <b-card no-body class="mb-1">
        <div class="m-2">
          <b-row>
            <b-col cols="12" md="6">
              <b-form-input
                v-model="filters.search"
                placeholder="Pesquisar..."
                size="sm"
                debounce="400"
                @update="loadAllOportunidades"
              />
            </b-col>
          </b-row>
        </div>
      </b-card>

      <div v-if="loading" class="text-center py-4">
        <b-spinner variant="primary" />
        <p class="mt-1 text-muted">Carregando oportunidades...</p>
      </div>

      <div v-else class="kanban-board">
        <b-card
          v-for="col in kanbanColumns"
          :key="col.value"
          no-body
          class="kanban-column mb-0"
        >
          <div class="d-flex justify-content-between align-items-center p-1 border-bottom">
            <strong>{{ col.text }}</strong>
            <div class="d-flex align-items-center" style="gap: 0.5rem;">
              <small class="text-muted">{{ formatCurrency(getColumnTotal(col.value)) }}</small>
              <b-badge variant="light-secondary" pill>{{ getColumnItems(col.value).length }}</b-badge>
            </div>
          </div>
          <div class="kanban-column-body p-1">
            <b-card
              v-for="oport in getColumnItems(col.value)"
              :key="oport.id"
              no-body
              class="mb-1 kanban-card"
              @click="openModal(oport)"
            >
              <div class="p-1">
                <div class="d-flex justify-content-between align-items-start mb-50">
                  <strong class="kanban-card-title">{{ oport.titulo }}</strong>
                  <b-dropdown
                    variant="link"
                    size="sm"
                    no-caret
                    right
                    toggle-class="p-0"
                    @click.native.stop
                  >
                    <template #button-content>
                      <feather-icon icon="MoreHorizontalIcon" size="16" />
                    </template>
                    <b-dropdown-item @click.stop="openModal(oport)">
                      <feather-icon icon="EditIcon" size="14" class="mr-50" /> Editar
                    </b-dropdown-item>
                    <b-dropdown-item
                      v-if="oport.cliente_id"
                      @click.stop="$router.push({ name: 'crm-cliente-detail', params: { id: oport.cliente_id } })"
                    >
                      <feather-icon icon="UserIcon" size="14" class="mr-50" /> Ver Cliente
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
                <div class="text-muted small mb-50 text-truncate">
                  {{ oport.cliente_nome || oport.cliente_razao_social || 'Sem cliente' }}
                </div>
                <div class="d-flex align-items-center flex-wrap" style="gap: 0.4rem;">
                  <b-badge :variant="getDaysBadgeVariant(oport)" pill>
                    {{ getDaysOpen(oport) }}d
                  </b-badge>
                  <small class="font-weight-bold">{{ formatCurrency(oport.valor_estimado) }}</small>
                  <small v-if="oport.data_prevista_fechamento" class="text-muted ml-auto">
                    <feather-icon icon="CalendarIcon" size="12" />
                    {{ formatDateShort(oport.data_prevista_fechamento) }}
                  </small>
                </div>
              </div>
            </b-card>
            <div v-if="getColumnItems(col.value).length === 0" class="text-center text-muted py-2">
              <small>Nenhuma oportunidade</small>
            </div>
          </div>
        </b-card>
      </div>
    </div>

    <!-- LISTA VIEW -->
    <div v-if="viewMode === 'lista'">
      <b-row class="mb-1">
        <b-col
          v-for="s in statusOptions"
          :key="s.value"
          cols="6"
          md="4"
          lg="2"
          class="mb-1"
        >
          <b-card
            no-body
            class="mb-0 pipeline-card"
            :border-variant="filters.status === s.value ? 'primary' : ''"
            @click="toggleStatusFilter(s.value)"
          >
            <div class="p-1 text-center">
              <h3 class="mb-25">{{ getPipelineCount(s.value) }}</h3>
              <b-badge :variant="getStatusBadgeVariant(s.value)" pill>{{ s.text }}</b-badge>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <b-card no-body class="mb-0">
        <div class="m-2">
          <b-row>
            <b-col cols="12" md="6" class="d-flex align-items-center mb-1 mb-md-0">
              <b-button v-b-toggle.crm-oport-filters-collapse variant="outline-primary" size="sm">
                <feather-icon icon="FilterIcon" size="14" class="mr-25" /> Filtros
              </b-button>
            </b-col>
            <b-col cols="12" md="6">
              <b-input-group>
                <b-form-input
                  v-model="filters.search"
                  placeholder="Título ou cliente..."
                  debounce="400"
                  @update="onFilterChange"
                />
                <b-input-group-append>
                  <b-button variant="outline-secondary" @click="clearFilters">
                    <feather-icon icon="TrashIcon" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-col>
          </b-row>

          <b-collapse id="crm-oport-filters-collapse" class="mt-2">
            <b-row>
              <b-col cols="12" md="4">
                <label>Status</label>
                <b-form-select v-model="filters.status" @change="onFilterChange">
                  <b-form-select-option :value="null">Todos</b-form-select-option>
                  <b-form-select-option
                    v-for="s in statusOptions"
                    :key="s.value"
                    :value="s.value"
                  >{{ s.text }}</b-form-select-option>
                </b-form-select>
              </b-col>
            </b-row>
          </b-collapse>
        </div>

        <b-table
          class="position-relative"
          :items="oportunidades"
          :fields="fields"
          :busy="loading"
          responsive
          show-empty
          primary-key="id"
          empty-text="Nenhuma oportunidade encontrada"
        >
          <template #table-busy>
            <div class="text-center py-4">
              <b-spinner variant="primary" />
              <p class="mt-1 text-muted">Carregando oportunidades...</p>
            </div>
          </template>

          <template #cell(cliente_nome)="{ item }">
            <b-link
              v-if="item.cliente_id"
              @click="$router.push({ name: 'crm-cliente-detail', params: { id: item.cliente_id } })"
            >
              {{ item.cliente_nome || item.cliente_razao_social || '-' }}
            </b-link>
            <span v-else>-</span>
          </template>

          <template #cell(status)="{ item }">
            <b-badge :variant="getStatusBadgeVariant(item.status)" pill>{{ item.status }}</b-badge>
          </template>

          <template #cell(valor_estimado)="{ item }">
            {{ formatCurrency(item.valor_estimado) }}
          </template>

          <template #cell(probabilidade)="{ item }">
            <div class="d-flex align-items-center" style="gap: 0.5rem;">
              <b-progress
                :value="item.probabilidade || 0"
                max="100"
                class="flex-grow-1"
                style="min-width: 60px; height: 6px;"
              />
              <small>{{ item.probabilidade != null ? item.probabilidade + '%' : '-' }}</small>
            </div>
          </template>

          <template #cell(data_prevista_fechamento)="{ item }">
            {{ item.data_prevista_fechamento ? formatDate(item.data_prevista_fechamento) : '-' }}
          </template>

          <template #cell(actions)="{ item }">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="gradient-primary"
              size="sm"
              class="btn-icon"
              title="Editar"
              @click="openModal(item)"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
          </template>
        </b-table>

        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted" v-if="totalRows > 0">
                Mostrando {{ ((currentPage - 1) * perPage) + 1 }}
                até {{ Math.min(currentPage * perPage, totalRows) }}
                de {{ totalRows }} entradas
              </span>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                @change="onPageChange"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-card>
    </div>

    <b-modal
      v-model="modal.show"
      :title="modal.editing ? 'Editar Oportunidade' : 'Nova Oportunidade'"
      centered
      ok-title="Salvar"
      cancel-title="Cancelar"
      @ok.prevent="saveOportunidade"
    >
      <b-form-group label="Título">
        <b-form-input v-model="modal.form.titulo" />
      </b-form-group>
      <b-form-group label="Cliente">
        <b-form-input
          v-model="modal.clienteSearch"
          placeholder="Buscar cliente..."
          @input="searchClientes"
        />
        <div
          v-if="modal.clienteSearch && (modal.clienteLoading || modal.clienteResults.length || modal.clienteNoResults)"
          class="search-results"
        >
          <div v-if="modal.clienteLoading" class="p-1">
            <b-spinner small variant="primary" class="mr-50" /> Buscando clientes...
          </div>
          <div v-else-if="modal.clienteNoResults" class="p-1 text-muted">
            Nenhum cliente encontrado
          </div>
          <div
            v-else
            v-for="c in modal.clienteResults"
            :key="c.clienteid"
            class="search-result-item p-1"
            @click="selectCliente(c)"
          >
            {{ c.clienterazaosocial }} <small class="text-muted">{{ c.clientecnpj }}</small>
          </div>
        </div>
        <small v-if="modal.form.cliente_id" class="text-muted">
          Cliente selecionado: {{ modal.clienteNome }}
        </small>
      </b-form-group>
      <b-form-group label="Status">
        <b-form-select v-model="modal.form.status">
          <b-form-select-option
            v-for="s in statusOptions"
            :key="s.value"
            :value="s.value"
          >{{ s.text }}</b-form-select-option>
        </b-form-select>
      </b-form-group>
      <b-row>
        <b-col md="6">
          <b-form-group label="Valor Estimado (R$)">
            <b-form-input v-model="modal.form.valor_estimado" type="number" step="0.01" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Probabilidade (%)">
            <b-form-input v-model="modal.form.probabilidade" type="number" min="0" max="100" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group label="Data Prevista Fechamento">
        <b-form-input v-model="modal.form.data_prevista_fechamento" type="date" />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import crmClient from '@/api/CRM/crm.client';
import {
  BCard, BTable, BPagination, BFormInput, BFormSelect,
  BFormSelectOption, BButton, BBadge, BSpinner, BRow, BCol,
  BFormGroup, BModal, BDropdown, BDropdownItem, BCollapse,
  BInputGroup, BInputGroupAppend, BLink, BProgress, VBToggle,
} from 'bootstrap-vue';
import Ripple from 'vue-ripple-directive';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';

export default {
  name: 'CrmOportunidadesPage',
  components: {
    BCard, BTable, BPagination, BFormInput, BFormSelect,
    BFormSelectOption, BButton, BBadge, BSpinner, BRow, BCol,
    BFormGroup, BModal, BDropdown, BDropdownItem, BCollapse,
    BInputGroup, BInputGroupAppend, BLink, BProgress,
  },
  directives: {
    'b-toggle': VBToggle,
    Ripple,
  },
  data() {
    return {
      viewMode: 'kanban',
      loading: false,
      oportunidades: [],
      allOportunidades: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 20,
      pipelineCounts: {},
      filters: {
        search: '',
        status: null,
      },
      fields: [
        { key: 'titulo', label: 'Título', sortable: true },
        { key: 'cliente_nome', label: 'Cliente' },
        { key: 'status', label: 'Status' },
        { key: 'valor_estimado', label: 'Valor Estimado', sortable: true },
        { key: 'probabilidade', label: 'Probabilidade', class: 'text-center' },
        { key: 'data_prevista_fechamento', label: 'Data Prev.', sortable: true },
        { key: 'actions', label: 'Ações', class: 'text-center' },
      ],
      statusOptions: [
        { value: 'ABERTA', text: 'Aberta' },
        { value: 'QUALIFICADA', text: 'Qualificada' },
        { value: 'EM_NEGOCIACAO', text: 'Em Negociação' },
        { value: 'GANHA', text: 'Ganha' },
        { value: 'PERDIDA', text: 'Perdida' },
        { value: 'PAUSADA', text: 'Pausada' },
      ],
      kanbanColumns: [
        { value: 'ABERTA', text: 'Aberta' },
        { value: 'QUALIFICADA', text: 'Qualificada' },
        { value: 'EM_NEGOCIACAO', text: 'Em Negociação' },
        { value: 'GANHA', text: 'Ganha' },
        { value: 'PERDIDA', text: 'Perdida' },
        { value: 'PAUSADA', text: 'Pausada' },
      ],
      modal: {
        show: false,
        editing: false,
        editId: null,
        form: {
          titulo: '',
          cliente_id: '',
          status: 'ABERTA',
          valor_estimado: '',
          probabilidade: '',
          data_prevista_fechamento: '',
        },
        clienteSearch: '',
        clienteNome: '',
        clienteResults: [],
        clienteLoading: false,
        clienteNoResults: false,
      },
      searchTimeout: null,
    };
  },
  created() {
    this.loadData();
  },
  methods: {
    switchView(mode) {
      this.viewMode = mode;
      this.loadData();
    },
    loadData() {
      if (this.viewMode === 'kanban') {
        this.loadAllOportunidades();
      } else {
        this.loadOportunidades();
      }
    },
    async loadAllOportunidades() {
      this.loading = true;
      try {
        const params = { page: 1, limit: 500 };
        if (this.filters.search) params.search = this.filters.search;

        const result = await crmClient.listOportunidades(params);
        const payload = result.data || result;
        this.allOportunidades = payload.items || payload.rows || (Array.isArray(payload) ? payload : []);
      } catch (error) {
        console.error('Erro ao carregar oportunidades:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar oportunidades',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async loadOportunidades() {
      this.loading = true;
      try {
        const params = { page: this.currentPage, limit: this.perPage };
        if (this.filters.search) params.search = this.filters.search;
        if (this.filters.status) params.status = this.filters.status;

        const result = await crmClient.listOportunidades(params);
        const payload = result.data || result;
        this.oportunidades = payload.items || payload.rows || (Array.isArray(payload) ? payload : []);
        this.totalRows = payload.total || payload.count || this.oportunidades.length;

        if (payload.pipeline) {
          this.pipelineCounts = payload.pipeline;
        } else {
          this.computePipelineCounts();
        }
      } catch (error) {
        console.error('Erro ao carregar oportunidades:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar oportunidades',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    getColumnItems(status) {
      return this.allOportunidades.filter(o => o.status === status);
    },
    getColumnTotal(status) {
      return this.getColumnItems(status).reduce((sum, o) => sum + Number(o.valor_estimado || 0), 0);
    },
    getDaysOpen(oport) {
      if (!oport.createdAt && !oport.created_at) return 0;
      const created = new Date(oport.createdAt || oport.created_at);
      const now = new Date();
      const diff = Math.floor((now - created) / (1000 * 60 * 60 * 24));
      return diff >= 0 ? diff : 0;
    },
    getDaysBadgeVariant(oport) {
      const days = this.getDaysOpen(oport);
      if (days <= 7) return 'light-success';
      if (days <= 30) return 'light-warning';
      return 'light-danger';
    },
    formatDateShort(val) {
      if (!val) return '';
      const d = new Date(val);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yy = String(d.getFullYear()).slice(-2);
      return `${dd}/${mm}/${yy}`;
    },
    computePipelineCounts() {
      const counts = {};
      this.statusOptions.forEach(s => { counts[s.value] = 0; });
      this.oportunidades.forEach(o => {
        if (counts[o.status] !== undefined) counts[o.status]++;
      });
      this.pipelineCounts = counts;
    },
    getPipelineCount(status) {
      return this.pipelineCounts[status] || 0;
    },
    toggleStatusFilter(status) {
      this.filters.status = this.filters.status === status ? null : status;
      this.currentPage = 1;
      this.loadOportunidades();
    },
    onFilterChange() {
      this.currentPage = 1;
      this.loadOportunidades();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadOportunidades();
    },
    clearFilters() {
      this.filters = { search: '', status: null };
      this.currentPage = 1;
      this.loadOportunidades();
    },
    openModal(oport) {
      if (oport) {
        this.modal.editing = true;
        this.modal.editId = oport.id;
        this.modal.form = {
          titulo: oport.titulo || '',
          cliente_id: oport.cliente_id || '',
          status: oport.status || 'ABERTA',
          valor_estimado: oport.valor_estimado || '',
          probabilidade: oport.probabilidade || '',
          data_prevista_fechamento: oport.data_prevista_fechamento ? oport.data_prevista_fechamento.slice(0, 10) : '',
        };
        this.modal.clienteNome = oport.cliente_nome || oport.cliente_razao_social || '';
      } else {
        this.modal.editing = false;
        this.modal.editId = null;
        this.modal.form = {
          titulo: '', cliente_id: '', status: 'ABERTA',
          valor_estimado: '', probabilidade: '', data_prevista_fechamento: '',
        };
        this.modal.clienteNome = '';
      }
      this.modal.clienteSearch = '';
      this.modal.clienteResults = [];
      this.modal.show = true;
    },
    async saveOportunidade() {
      try {
        const payload = { ...this.modal.form };
        if (!payload.cliente_id) delete payload.cliente_id;

        if (this.modal.editing) {
          await crmClient.updateOportunidade(this.modal.editId, payload);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Oportunidade atualizada',
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          });
        } else {
          await crmClient.createOportunidade(payload);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Oportunidade criada',
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          });
        }
        this.modal.show = false;
        this.loadData();
      } catch (error) {
        console.error('Erro ao salvar oportunidade:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao salvar oportunidade',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    searchClientes(query) {
      clearTimeout(this.searchTimeout);
      if (!query || query.length < 2) {
        this.modal.clienteResults = [];
        this.modal.clienteLoading = false;
        this.modal.clienteNoResults = false;
        return;
      }
      this.searchTimeout = setTimeout(async () => {
        this.modal.clienteLoading = true;
        this.modal.clienteNoResults = false;
        try {
          const result = await crmClient.listClientes({ query: query, limit: 10 });
          const payload = result.data || result;
          this.modal.clienteResults = payload.items || payload.rows || (Array.isArray(payload) ? payload : []);
          this.modal.clienteNoResults = this.modal.clienteResults.length === 0;
        } catch (e) {
          this.modal.clienteResults = [];
          this.modal.clienteNoResults = true;
        } finally {
          this.modal.clienteLoading = false;
        }
      }, 300);
    },
    selectCliente(c) {
      this.modal.form.cliente_id = c.clienteid;
      this.modal.clienteNome = c.clienterazaosocial;
      this.modal.clienteSearch = '';
      this.modal.clienteResults = [];
    },
    getStatusBadgeVariant(status) {
      const map = {
        ABERTA: 'light-info',
        QUALIFICADA: 'light-primary',
        EM_NEGOCIACAO: 'light-warning',
        GANHA: 'light-success',
        PERDIDA: 'light-danger',
        PAUSADA: 'light-secondary',
      };
      return map[status] || 'light-secondary';
    },
    formatCurrency(value) {
      return 'R$ ' + Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(val) {
      if (!val) return '-';
      return new Date(val).toLocaleDateString('pt-BR');
    },
  },
};
</script>

<style lang="scss" scoped>
.kanban-board {
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  padding-bottom: 1rem;
  align-items: flex-start;
}
.kanban-column {
  min-width: 280px;
  max-width: 320px;
  flex: 1 0 280px;
  max-height: calc(100vh - 220px);
  display: flex;
  flex-direction: column;
}
.kanban-column-body {
  overflow-y: auto;
  flex: 1;
}
.kanban-card {
  cursor: pointer;
}
.kanban-card-title {
  font-size: 0.85rem;
  line-height: 1.3;
  flex: 1;
  margin-right: 0.25rem;
}
.pipeline-card {
  cursor: pointer;
}
.search-results {
  position: absolute;
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
  width: calc(100% - 2rem);
}
.search-result-item {
  cursor: pointer;
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .kanban-board {
    flex-direction: column;
  }
  .kanban-column {
    min-width: 100%;
    max-width: 100%;
    max-height: none;
  }
}
</style>
